import React from 'react';
import { MarketingPage, MarketingContent } from '../components/MarketingPage';

const content: MarketingContent = {
  seo: {
    title: 'Local Network File Sharing — Fast, Private LAN Transfers',
    description:
      'Share files directly between devices on your network at full LAN speed — no internet round-trip, no third-party cloud. Windows, Mac, Linux, iPhone, and Android. Free for 2 devices.',
    path: '/local-network-file-sharing',
  },
  eyebrow: 'LAN file transfer',
  h1: <>Local network file sharing, <span className="em">done right.</span></>,
  lead:
    'Send files straight between the devices on your own network — at full local-network speed, with nothing routed through the internet or parked on someone else’s cloud.',
  sections: [
    {
      h2: 'What “local network file sharing” means',
      paras: [
        'When two devices are on the same network — your home or office Wi-Fi — they can talk to each other directly. Local network file sharing moves a file over that direct path instead of uploading it to a server on the internet and pulling it back down somewhere else.',
        'That direct path is both faster and more private: the data never leaves your network, and no outside company holds a copy.',
      ],
    },
    {
      h2: 'Why local beats the cloud for moving your own files',
      bullets: [
        <><b>Speed:</b> LAN transfers run at your network’s full speed — no waiting on an upload, then a download.</>,
        <><b>Privacy:</b> the file stays on your network; nothing is stored, indexed, or scanned by a third party.</>,
        <><b>No quotas:</b> you’re not paying for storage tiers to move your own files around.</>,
        <><b>Works offline:</b> as long as your devices share a network, you don’t need internet at all.</>,
      ],
    },
    {
      h2: 'How Local Area Cloud does it',
      paras: [
        'Local Area Cloud is built around this idea. Each of your devices registers to your free account so they can discover one another; then transfers go device-to-device over the LAN. Our cloud service is only a registry — a way for your devices to find each other. It never receives your file contents.',
        'It runs on Windows, macOS, Linux, iPhone, and Android, so every device you own can join the same private network of your own gear.',
      ],
    },
    {
      h2: 'More than one-off sends',
      paras: [
        'Because your devices stay registered to your account, Local Area Cloud can also run scheduled automatic backups — quietly copying a folder from one device to another on your network, on a schedule you set. It’s the same private, local path, working for you in the background.',
      ],
    },
  ],
  faqs: [
    {
      q: 'Do both devices have to be on the same network?',
      a: 'For local (free) transfers, yes — both devices should be on the same LAN or Wi-Fi. Pro adds secure transfers across networks using your own VPN.',
    },
    {
      q: 'Is it faster than a cloud drive?',
      a: 'Usually much faster. A cloud drive has to upload your file and then download it again; a local transfer sends it straight across your network in one hop.',
    },
    {
      q: 'Does it work without internet?',
      a: 'Yes for transfers between devices already signed in, as long as they share a network. Internet is only needed to sign in and let new devices discover each other.',
    },
    {
      q: 'Which platforms are supported?',
      a: 'Windows, macOS, Linux, iPhone, and Android — any of your devices can share files with any other.',
    },
    {
      q: 'Is it free?',
      a: 'Yes, free for up to two devices with every feature. Pro unlocks unlimited devices and cross-network reach.',
    },
  ],
  related: [
    { label: 'AirDrop for Windows', to: '/airdrop-for-windows', note: 'Cross-platform instant send' },
    { label: 'Transfer files between phone and PC', to: '/transfer-files-phone-to-pc', note: 'The phone ⇄ PC job' },
    { label: 'File sharing without the cloud', to: '/file-sharing-without-the-cloud', note: 'Keep files off servers' },
  ],
  ctaBand: {
    h2: 'Your network is faster than the cloud.',
    p: 'Move files at LAN speed, privately, between all your devices. Free for up to two.',
  },
};

export const LocalNetworkFileSharing: React.FC = () => <MarketingPage content={content} />;
export default LocalNetworkFileSharing;
